// The shared Athavita top bar (approved suite-shell mock-up, 2026-09-15): one
// 48 px bar for every app — apps launcher, app tile + name, an optional editor
// title area, search, the app's own buttons, then AI, notifications,
// Appearance, help and the account menu. Colours follow the suite appearance
// (accent palette + top-bar style; `.suite-appbar*` classes in globals.css).
//
// Self-contained (plain React + Tailwind + lucide) so every SPA can use it
// whatever component kit it has. Tailwind must scan Shared/ui-theme/src.

import {
    forwardRef,
    useEffect,
    useId,
    useRef,
    useState,
    type ButtonHTMLAttributes,
    type ReactNode,
    type RefObject
} from "react";
import { Bell, Check, CircleHelp, Cloud, Grip, Keyboard, LogOut, Palette, Settings, Sparkles } from "lucide-react";
import { PALETTES, useSuiteAppearance, type AppbarStyle, type SuiteAppKey, type ThemeMode } from "./appearance";
import { presenceDotClass, type PresenceStatus } from "./presence";
import { SUITE_APPS, suiteApp, suiteAppUrl, type SuiteAppId } from "./suite-apps";

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(" ");

export function initialsOf(name: string): string {
    const clean = name.replace(/\s*[([<].*?[)\]>]\s*/g, " ").replace(/@.*/, "").trim();
    const words = clean.split(/[\s._-]+/).filter(Boolean);
    if (words.length === 0) return "?";
    if (words.length === 1) return (words[0] as string).slice(0, 2).toUpperCase();
    return ((words[0] as string)[0]! + (words[words.length - 1] as string)[0]!).toUpperCase();
}

// ── Popover plumbing ─────────────────────────────────────────────────────

/** Outside click or Esc closes; Esc returns focus to the trigger. */
function useDismissable(open: boolean, close: () => void, wrap: RefObject<HTMLElement>, trigger: RefObject<HTMLElement>) {
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (wrap.current && !wrap.current.contains(e.target as Node)) close();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            e.stopPropagation();
            close();
            trigger.current?.focus();
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open, close, wrap, trigger]);
}

const panelClass = "absolute top-full z-50 mt-1.5 rounded-lg border bg-popover p-3 text-popover-foreground shadow-xl";

// ── Buttons ──────────────────────────────────────────────────────────────

type BarButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { label: string; pressed?: boolean; dot?: boolean };

/** A 36 px icon button on the top bar. */
export const SuiteBarButton = forwardRef<HTMLButtonElement, BarButtonProps>(function SuiteBarButton(
    { label, pressed, dot, className, children, ...rest },
    ref
) {
    return (
        <button
            ref={ref}
            type="button"
            aria-label={label}
            title={label}
            aria-pressed={pressed}
            className={cx("suite-appbar-btn relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded", className)}
            {...rest}
        >
            {children}
            {dot ? <span aria-hidden className="suite-appbar-dot absolute right-[7px] top-[7px] h-2 w-2 rounded-full" /> : null}
        </button>
    );
});

/** The app's coloured tile (launcher, brand, file cards). */
export function SuiteAppTile({ app, size = 24, className }: { app: SuiteAppId; size?: number; className?: string }) {
    const info = suiteApp(app);
    const Icon = info.icon;
    return (
        <span
            aria-hidden
            className={cx("inline-flex shrink-0 items-center justify-center text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)]", className)}
            style={{ width: size, height: size, borderRadius: Math.round(size / 4.5), background: info.color }}
        >
            <Icon style={{ width: Math.round(size * 0.62), height: Math.round(size * 0.62) }} strokeWidth={2} />
        </span>
    );
}

// ── Launcher ─────────────────────────────────────────────────────────────

const LAUNCHER_APPS: readonly SuiteAppId[] = ["mail", "drive", "docs", "sheets", "slides", "pdf", "connect"];

/** Waffle: every app as a tile; opens it in a new tab. The current app is marked, not a link. */
export function SuiteAppLauncher({ current }: { current: SuiteAppKey }) {
    const [open, setOpen] = useState(false);
    const wrap = useRef<HTMLDivElement>(null);
    const trigger = useRef<HTMLButtonElement>(null);
    useDismissable(open, () => setOpen(false), wrap, trigger);

    return (
        <div ref={wrap} className="relative">
            <SuiteBarButton ref={trigger} label="Athavita apps" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
                <Grip className="h-5 w-5" aria-hidden />
            </SuiteBarButton>
            {open ? (
                <nav aria-label="Athavita apps" className={cx(panelClass, "left-0 w-[min(21.5rem,calc(100vw-1rem))]")}>
                    <div className="px-1 pb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Athavita apps</div>
                    <div className="grid grid-cols-4 gap-1">
                        {LAUNCHER_APPS.map((id) => {
                            const info = suiteApp(id);
                            const here = id === current;
                            const body = (
                                <>
                                    <SuiteAppTile app={id} size={38} />
                                    <span className="text-xs">{info.name}</span>
                                </>
                            );
                            const cls = "flex flex-col items-center gap-1.5 rounded-md px-1 py-2.5 text-center outline-none focus-visible:ring-2 focus-visible:ring-ring";
                            return here ? (
                                <div key={id} aria-current="page" title={`${info.name} — you're here`} className={cx(cls, "bg-primary/10 font-semibold")}>
                                    {body}
                                </div>
                            ) : (
                                <a
                                    key={id}
                                    href={suiteAppUrl(id)}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    title={`${info.name} — ${info.blurb}`}
                                    onClick={() => setOpen(false)}
                                    className={cx(cls, "hover:bg-accent")}
                                >
                                    {body}
                                </a>
                            );
                        })}
                    </div>
                    <div className="mt-2 flex items-center gap-1 border-t pt-2 text-xs">
                        {(["account", "admin"] as const).map((id) => (
                            <a
                                key={id}
                                href={suiteAppUrl(id)}
                                target="_blank"
                                rel="noreferrer noopener"
                                onClick={() => setOpen(false)}
                                className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                                <SuiteAppTile app={id} size={18} />
                                {suiteApp(id).name}
                            </a>
                        ))}
                        <span className="ml-auto pr-1 text-[11px] text-muted-foreground">Opens in a new tab</span>
                    </div>
                </nav>
            ) : null}
        </div>
    );
}

// ── Appearance ───────────────────────────────────────────────────────────

const MODE_OPTIONS: ReadonlyArray<readonly [ThemeMode, string]> = [["light", "Light"], ["dark", "Dark"], ["system", "Match system"]];
const APPBAR_OPTIONS: ReadonlyArray<readonly [AppbarStyle, string]> = [["accent", "Accent"], ["light", "Light"], ["dark", "Dark"]];

function Segmented<T extends string>({ label, value, options, onChange }: {
    label: string;
    value: T;
    options: ReadonlyArray<readonly [T, string]>;
    onChange: (v: T) => void;
}) {
    return (
        <div role="radiogroup" aria-label={label} className="flex gap-0.5 rounded-md border p-0.5">
            {options.map(([v, text]) => (
                <button
                    key={v}
                    type="button"
                    role="radio"
                    aria-checked={value === v}
                    onClick={() => onChange(v)}
                    className={cx(
                        "flex min-h-7 flex-1 items-center justify-center rounded px-2 text-xs font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        value === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                    )}
                >
                    {text}
                </button>
            ))}
        </div>
    );
}

const sectionTitle = "mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground";

/**
 * The Appearance controls: "Different look in <app>" switch, mode, accent and
 * top-bar style. A change applies to every Athavita app unless this app has
 * its own look. Used in the top-bar popover and on settings pages.
 */
export function AppearancePanel({ app }: { app: SuiteAppKey }) {
    const { look, resolved, hasOwnLook, setLook, setOwnLook } = useSuiteAppearance(app);
    const name = suiteApp(app).name;
    const dark = resolved === "dark";
    const switchId = useId();

    return (
        <div className="space-y-3">
            <div className="flex items-start gap-3 rounded-md border bg-muted/50 p-2.5">
                <label htmlFor={switchId} className="min-w-0 flex-1 cursor-pointer text-sm">
                    <span className="block font-semibold">{hasOwnLook ? `${name} has its own look` : "Same look in every app"}</span>
                    <span className="block text-xs text-muted-foreground">
                        {hasOwnLook
                            ? `Changes here stay in ${name}. Switch off to go back to the suite look.`
                            : `Changes apply to every Athavita app. Switch on to give ${name} its own.`}
                    </span>
                </label>
                <button
                    id={switchId}
                    type="button"
                    role="switch"
                    aria-checked={hasOwnLook}
                    aria-label={`Different look in ${name}`}
                    onClick={() => setOwnLook(!hasOwnLook)}
                    className={cx(
                        "relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                        hasOwnLook ? "bg-primary" : "bg-muted-foreground/40"
                    )}
                >
                    <span className={cx("absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-[left]", hasOwnLook ? "left-[18px]" : "left-0.5")} />
                </button>
            </div>

            <div>
                <div className={sectionTitle}>Mode</div>
                <Segmented label="Mode" value={look.mode} options={MODE_OPTIONS} onChange={(mode) => setLook({ mode })} />
            </div>

            <div>
                <div className={sectionTitle}>Accent colour</div>
                <div role="radiogroup" aria-label="Accent colour" className="grid grid-cols-4 gap-1">
                    {PALETTES.map((p) => {
                        const on = look.palette === p.id;
                        return (
                            <button
                                key={p.id}
                                type="button"
                                role="radio"
                                aria-checked={on}
                                onClick={() => setLook({ palette: p.id })}
                                className={cx(
                                    "flex flex-col items-center gap-1 rounded-md px-0.5 py-1.5 text-[11px] leading-tight outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring",
                                    on ? "font-semibold text-foreground" : "text-muted-foreground"
                                )}
                            >
                                <span
                                    aria-hidden
                                    className={cx(
                                        "flex h-7 w-7 items-center justify-center rounded-full ring-1 ring-inset ring-black/10",
                                        on && "outline outline-2 outline-offset-2 outline-foreground"
                                    )}
                                    style={{ background: dark ? p.dark : p.light }}
                                >
                                    {on ? <Check className={cx("h-4 w-4", dark ? "text-slate-900" : "text-white")} /> : null}
                                </span>
                                <span className="text-center">{p.name}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div>
                <div className={sectionTitle}>Top bar</div>
                <Segmented label="Top bar" value={look.appbar} options={APPBAR_OPTIONS} onChange={(appbar) => setLook({ appbar })} />
            </div>

            <p className="flex gap-1.5 text-xs text-muted-foreground">
                <Cloud className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
                Saved to your account and applied before any app draws, on every device.
            </p>
        </div>
    );
}

/** Top-bar Appearance button + popover. `buttonClassName` lets an app match its own bar buttons. */
export function AppearanceMenu({ app, buttonClassName }: { app: SuiteAppKey; buttonClassName?: string }) {
    const [open, setOpen] = useState(false);
    const wrap = useRef<HTMLDivElement>(null);
    const trigger = useRef<HTMLButtonElement>(null);
    useDismissable(open, () => setOpen(false), wrap, trigger);
    return (
        <div ref={wrap} className="relative">
            <SuiteBarButton
                ref={trigger}
                label="Appearance"
                aria-haspopup="dialog"
                aria-expanded={open}
                data-state={open ? "open" : "closed"}
                onClick={() => setOpen((v) => !v)}
                className={buttonClassName}
            >
                <Palette className="h-5 w-5" aria-hidden />
            </SuiteBarButton>
            {open ? (
                <div role="dialog" aria-label="Appearance" className={cx(panelClass, "right-0 w-[min(20rem,calc(100vw-1rem))]")}>
                    <AppearancePanel app={app} />
                </div>
            ) : null}
        </div>
    );
}

// ── Account menu ─────────────────────────────────────────────────────────

export interface SuiteUser {
    name: string;
    email?: string;
    presence?: PresenceStatus;
}

export function SuiteAvatar({ user, size = 32, ring }: { user: SuiteUser; size?: number; ring?: boolean }) {
    return (
        <span
            className="suite-avatar relative inline-flex shrink-0 items-center justify-center rounded-full font-semibold"
            style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
        >
            {initialsOf(user.name || user.email || "?")}
            {user.presence ? (
                <span
                    aria-hidden
                    className={cx(
                        "absolute -bottom-px -right-px h-2.5 w-2.5 rounded-full border-2",
                        ring ? "border-[var(--appbar-bg)]" : "border-popover",
                        presenceDotClass(user.presence)
                    )}
                />
            ) : null}
        </span>
    );
}

export function SuiteMenuItem({
    icon: Icon,
    children,
    hint,
    danger,
    onClick
}: {
    icon?: React.ComponentType<{ className?: string }>;
    children: ReactNode;
    hint?: ReactNode;
    danger?: boolean;
    onClick?: () => void;
}) {
    return (
        <button
            type="button"
            role="menuitem"
            onClick={onClick}
            className={cx(
                "flex w-full items-center gap-2.5 rounded px-2 py-2 text-left text-sm outline-none hover:bg-accent focus-visible:bg-accent",
                danger && "text-destructive"
            )}
        >
            {Icon ? <Icon className="h-4 w-4 shrink-0 opacity-80" /> : null}
            <span className="min-w-0 flex-1 truncate">{children}</span>
            {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
        </button>
    );
}

export function SuiteAccountMenu({
    user,
    sections,
    onSettings,
    settingsLabel,
    onShortcuts,
    onSignOut
}: {
    user: SuiteUser;
    /** App-specific rows (e.g. Connect's status), shown under the name. Close the menu via the render arg. */
    sections?: (close: () => void) => ReactNode;
    onSettings?: () => void;
    settingsLabel?: string;
    onShortcuts?: () => void;
    onSignOut: () => void;
}) {
    const [open, setOpen] = useState(false);
    const wrap = useRef<HTMLDivElement>(null);
    const trigger = useRef<HTMLButtonElement>(null);
    const close = () => setOpen(false);
    useDismissable(open, close, wrap, trigger);
    const run = (fn?: () => void) => () => {
        close();
        fn?.();
    };
    return (
        <div ref={wrap} className="relative">
            <button
                ref={trigger}
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={`Account menu for ${user.name}`}
                title={user.email ? `${user.name} (${user.email})` : user.name}
                onClick={() => setOpen((v) => !v)}
                className="suite-appbar-btn ml-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
            >
                <SuiteAvatar user={user} ring />
            </button>
            {open ? (
                <div role="menu" aria-label="Account" className={cx(panelClass, "right-0 w-[min(18.5rem,calc(100vw-1rem))] p-1.5")}>
                    <div className="flex items-center gap-3 px-2 pb-2.5 pt-1.5">
                        <SuiteAvatar user={{ ...user, presence: undefined }} size={44} />
                        <div className="min-w-0">
                            <div className="truncate text-sm font-semibold">{user.name}</div>
                            {user.email ? <div className="truncate text-xs text-muted-foreground">{user.email}</div> : null}
                            <a
                                href={suiteAppUrl("account")}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="text-xs font-semibold text-primary hover:underline"
                            >
                                View account
                            </a>
                        </div>
                    </div>
                    {sections ? <div className="border-t py-1">{sections(close)}</div> : null}
                    <div className="border-t py-1">
                        {onSettings ? <SuiteMenuItem icon={Settings} onClick={run(onSettings)} hint={settingsLabel}>Settings</SuiteMenuItem> : null}
                        {onShortcuts ? <SuiteMenuItem icon={Keyboard} onClick={run(onShortcuts)} hint="?">Keyboard shortcuts</SuiteMenuItem> : null}
                    </div>
                    <div className="border-t pt-1">
                        <SuiteMenuItem icon={LogOut} danger onClick={run(onSignOut)}>Sign out</SuiteMenuItem>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

// ── The bar ──────────────────────────────────────────────────────────────

export interface SuiteAppBarProps {
    app: SuiteAppKey;
    user: SuiteUser;
    onSignOut: () => void;
    /** Wraps the tile + name, e.g. in a router <Link to="/">. Default: plain text. */
    renderBrand?: (brand: ReactNode) => ReactNode;
    /** Before the launcher — e.g. a mobile "open navigation" button. */
    leading?: ReactNode;
    /** Editors: file name, Drive location, save state (a flex region after the brand). */
    title?: ReactNode;
    /** Search field (use SuiteSearchField or an input with `suite-appbar-input`). */
    search?: ReactNode;
    /** The app's own buttons, before the common ones (e.g. co-authors, Share, New meeting). */
    actions?: ReactNode;
    onAi?: () => void;
    onNotifications?: () => void;
    notificationDot?: boolean;
    onHelp?: () => void;
    onSettings?: () => void;
    onShortcuts?: () => void;
    accountSections?: (close: () => void) => ReactNode;
    className?: string;
}

export function SuiteAppBar({
    app,
    user,
    onSignOut,
    renderBrand,
    leading,
    title,
    search,
    actions,
    onAi,
    onNotifications,
    notificationDot,
    onHelp,
    onSettings,
    onShortcuts,
    accountSections,
    className
}: SuiteAppBarProps) {
    const info = suiteApp(app);
    const brand = (
        <span className="flex items-center gap-2 whitespace-nowrap">
            <SuiteAppTile app={app} size={24} />
            <span className="suite-appbar-brand hidden text-[15px] font-semibold sm:inline">Athavita {info.name}</span>
        </span>
    );
    return (
        <header className={cx("suite-appbar relative z-40 flex h-12 shrink-0 items-center gap-1.5 pl-1.5 pr-2 sm:gap-2 sm:pr-3", className)}>
            {leading}
            <SuiteAppLauncher current={app} />
            <div className="shrink-0 pr-1">
                {renderBrand ? renderBrand(brand) : brand}
            </div>
            {title ? (
                <div className={cx("suite-appbar-divider flex min-w-0 items-center gap-1.5 pl-2.5", !search && "flex-1")}>{title}</div>
            ) : null}
            {search ? <div className="flex min-w-0 flex-1 justify-center px-1 sm:px-3">{search}</div> : !title ? <div className="flex-1" /> : null}
            <div className="flex shrink-0 items-center gap-0.5">
                {actions}
                {onAi ? (
                    <SuiteBarButton label="Athavita AI" className="hidden sm:inline-flex" onClick={onAi}>
                        <Sparkles className="h-5 w-5" aria-hidden />
                    </SuiteBarButton>
                ) : null}
                {onNotifications ? (
                    <SuiteBarButton label="Notifications" className="hidden sm:inline-flex" dot={notificationDot} onClick={onNotifications}>
                        <Bell className="h-5 w-5" aria-hidden />
                    </SuiteBarButton>
                ) : null}
                <AppearanceMenu app={app} />
                {onHelp ? (
                    <SuiteBarButton label="Help and shortcuts" className="hidden md:inline-flex" onClick={onHelp}>
                        <CircleHelp className="h-5 w-5" aria-hidden />
                    </SuiteBarButton>
                ) : null}
                <SuiteAccountMenu
                    user={user}
                    sections={accountSections}
                    onSettings={onSettings}
                    settingsLabel={info.name}
                    onShortcuts={onShortcuts}
                    onSignOut={onSignOut}
                />
            </div>
        </header>
    );
}

/** Search input styled for the top bar, with an optional Ctrl K hint. Put suggestion panels in `children`. */
export const SuiteSearchField = forwardRef<
    HTMLInputElement,
    React.InputHTMLAttributes<HTMLInputElement> & { hint?: string; onHint?: () => void; children?: ReactNode; icon?: ReactNode }
>(function SuiteSearchField({ hint, onHint, children, icon, className, ...input }, ref) {
    return (
        <div className="relative w-full max-w-[560px]">
            <span className="suite-appbar-input-muted pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2">{icon}</span>
            <input
                ref={ref}
                type="search"
                autoComplete="off"
                className={cx(
                    "suite-appbar-input h-8 w-full rounded border-0 pl-9 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--appbar-fg)_55%,transparent)]",
                    hint ? "pr-16" : "pr-3",
                    className
                )}
                {...input}
            />
            {hint ? (
                <button
                    type="button"
                    tabIndex={-1}
                    onClick={onHint}
                    className="suite-appbar-input-muted absolute right-2 top-1/2 hidden -translate-y-1/2 rounded border px-1.5 text-[11px] leading-[18px] md:block"
                >
                    {hint}
                </button>
            ) : null}
            {children}
        </div>
    );
});

/** Every app's catalogue entry, for "Open in…" menus. */
export { SUITE_APPS };
